/** Background Tasks result panel for a product sync (`sync_products`,
 *  `sync_products_with_approval`) and its dry run (`fetch_products`): what the
 *  run did, or would do, with combined, split-off and left-out products (plan
 *  2026-10-11 product-sync joins §4, §4a, §5, §5a). Read from the task's
 *  archived result, the run's merged context.
 *
 *  - **Not built** (`omitted`): products the re-sync guard left out, with the
 *    tour their key is an additional ID of.
 *  - **Combined**: a dry run shows the build's plan per group (`joins_preview`:
 *    which IDs are new, already there or moving, and the problems); an import
 *    shows the outcome (`joins`: created, already, moved, skipped, errors).
 *  - **Split off**: a dry run lists the consented split-offs (the params'
 *    `split_off`); an import shows the outcome (`split_off`: done, orphaned,
 *    errors).
 *  - **Booking moves** (`booking_moves`, `booking_move_task_ids`): the
 *    Edit-booking tasks launched to move moved members' bookings, each linking
 *    back through its own `launched_by_task_id` argument, and the moves that
 *    launched nothing (no bookings to move, or refused).
 *
 *  Catalogue mode (Ventrata, plan §9, B8b) adds, from the result's
 *  `post_import`:
 *  - **Catalogue joins** (`catalogue_joins`): products whose `tour.join` names
 *    a product this run imported. The import writes their IDs onto its tour as
 *    the user who saved each join: written (new IDs, IDs moved off other
 *    tours; `recreated` when it put back IDs the join held before, deleted
 *    since, most likely by hand), already there, still waiting for the tour,
 *    refused (the joiner is marked `join_refused`), booking moves skipped; and
 *    the booking moves it launched, in a section of their own.
 *  - **Cleared joins** (`split_offs`): the imported products whose join was
 *    cleared, split off the target's tour (`cleared`) or still pending (the
 *    next sync retries). The split-offs themselves, with the other IDs that
 *    moved with the key or were left behind (`split_off.done[].aliases`,
 *    `aliases_left`), are in **Split off**.
 *
 *  Each section is one line of counts that expands to a table. Every tour is
 *  named and linked, as the result names it next to its id. Renders nothing
 *  for a run with none of these, or with no stored result: most syncs combine
 *  nothing, and their details should not grow an empty box.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import type {
  SyncProductsBookingMoveLaunch,
  SyncProductsBookingMoves,
  SyncProductsCatalogueJoined,
  SyncProductsCatalogueJoinRefusal,
  SyncProductsCatalogueJoins,
  SyncProductsCatalogueSplitOffs,
  SyncProductsHolder,
  SyncProductsJoinPlan,
  SyncProductsJoinsOutcome,
  SyncProductsOmitted,
  SyncProductsSplitOffOutcome,
  SyncProductsSplitOffPlan,
} from '@/api/sync';

// ---------------------------------------------------------------------------
// Reading the result
// ---------------------------------------------------------------------------

/** The parts of a product-sync result this panel shows, each `[]` / null when
 *  absent. A plan (`joinPlan`, `splitPlan`) is kept only when the run has no
 *  outcome for it: an import's context still carries the build's preview and
 *  is read for what actually happened instead. */
export interface SyncResultView {
  omitted: SyncProductsOmitted[];
  membersNotBuilt: number;
  joinPlan: SyncProductsJoinPlan[];
  joins: SyncProductsJoinsOutcome | null;
  splitPlan: SyncProductsSplitOffPlan[];
  splitOff: SyncProductsSplitOffOutcome | null;
  /** What became of each requested booking move; null for a run without any. */
  bookingMoves: SyncProductsBookingMoves | null;
  bookingMoveTaskIds: string[];
  /** Catalogue mode: the joins this import wrote (`post_import.catalogue_joins`). */
  catalogueJoins: CatalogueJoinsView | null;
  /** Catalogue mode: the cleared joins it settled (`post_import.split_offs`). */
  catalogueSplitOffs: CatalogueSplitOffsView | null;
}

/** `post_import.catalogue_joins`, every list `[]` when absent. */
export interface CatalogueJoinsView {
  joined: SyncProductsCatalogueJoined[];
  unchanged: string[];
  deferred: string[];
  refused: SyncProductsCatalogueJoinRefusal[];
  bookingMovesSkipped: NonNullable<SyncProductsCatalogueJoins['booking_moves_skipped']>;
  bookingMoves: SyncProductsBookingMoves | null;
  /** Why the step did not run: `time_limit`, or the error. */
  stopped: string | null;
  error: string | null;
}

/** `post_import.split_offs`, every list `[]` when absent. */
export interface CatalogueSplitOffsView {
  cleared: string[];
  pending: NonNullable<SyncProductsCatalogueSplitOffs['pending']>;
  stopped: string | null;
  error: string | null;
}

const listOf = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const recordOf = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const textOf = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const idsOf = (v: unknown): string[] => listOf<unknown>(v).map(String).filter(Boolean);

function movesOf(v: unknown): SyncProductsBookingMoves | null {
  const moves = recordOf(v);
  return (
    moves && {
      launched: listOf(moves.launched),
      skipped: listOf(moves.skipped),
      errors: listOf(moves.errors),
    }
  );
}

export function readSyncResult(raw: Record<string, unknown> | undefined): SyncResultView {
  const r = raw ?? {};
  const joins = recordOf(r.joins);
  const split = recordOf(r.split_off);
  const post = recordOf(r.post_import);
  const catalogueJoins = recordOf(post?.catalogue_joins);
  const catalogueSplits = recordOf(post?.split_offs);
  const omitted = listOf<SyncProductsOmitted>(r.omitted);
  const leftOut = new Set(omitted.map((o) => o.product_id));
  return {
    omitted,
    membersNotBuilt: typeof r.members_not_built === 'number' ? r.members_not_built : 0,
    joinPlan: joins ? [] : listOf(r.joins_preview),
    joins: joins && {
      created: listOf(joins.created),
      already: listOf(joins.already),
      moved: listOf(joins.moved),
      skipped: listOf(joins.skipped),
      errors: listOf(joins.errors),
    },
    // A consented product the build left out after all is not split off.
    splitPlan: split
      ? []
      : listOf<SyncProductsSplitOffPlan>(r.split_off).filter((x) => !leftOut.has(x.product_id)),
    splitOff: split && {
      done: listOf(split.done),
      orphaned: listOf(split.orphaned),
      errors: listOf(split.errors),
    },
    bookingMoves: movesOf(r.booking_moves),
    bookingMoveTaskIds: idsOf(r.booking_move_task_ids),
    catalogueJoins: catalogueJoins && {
      joined: listOf(catalogueJoins.joined),
      unchanged: idsOf(catalogueJoins.unchanged),
      deferred: idsOf(catalogueJoins.deferred),
      refused: listOf(catalogueJoins.refused),
      bookingMovesSkipped: listOf(catalogueJoins.booking_moves_skipped),
      bookingMoves: movesOf(catalogueJoins.booking_moves),
      stopped: textOf(catalogueJoins.stopped),
      error: textOf(catalogueJoins.error),
    },
    catalogueSplitOffs: catalogueSplits && {
      cleared: idsOf(catalogueSplits.cleared),
      pending: listOf(catalogueSplits.pending),
      stopped: textOf(catalogueSplits.stopped),
      error: textOf(catalogueSplits.error),
    },
  };
}

function isEmpty(v: SyncResultView): boolean {
  return (
    v.omitted.length === 0 &&
    v.joinPlan.length === 0 &&
    v.joins === null &&
    v.splitPlan.length === 0 &&
    v.splitOff === null &&
    v.bookingMoves === null &&
    v.bookingMoveTaskIds.length === 0 &&
    v.catalogueJoins === null &&
    v.catalogueSplitOffs === null
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export function SyncProductsResultPanel({ taskId, dryRun = false }: { taskId: string; dryRun?: boolean }) {
  const q = useQuery({
    queryKey: ['sync-products-result', taskId],
    queryFn: () => bgtasksApi.result(taskId),
    // The archive lands moments after completion; a run with no archive at
    // all answers 404 for good, so only a couple of tries.
    retry: (failureCount, error) =>
      failureCount < 2 && error instanceof ConsoleApiError && error.status === 404,
    retryDelay: 1200,
    staleTime: Infinity,
  });
  if (!q.data) return null;
  const view = readSyncResult(q.data.result);
  if (isEmpty(view)) return null;
  return (
    <Box>
      <Typography variant="subtitle2">Combined, split-off and left-out products</Typography>
      {dryRun && (
        <Typography variant="caption" color="text.secondary" component="div">
          A dry run: what the import would do. Nothing was written.
        </Typography>
      )}
      <Stack spacing={1} sx={{ mt: 1 }}>
        {view.omitted.length > 0 && <OmittedSection omitted={view.omitted} />}
        {view.joins ? (
          <JoinsSection joins={view.joins} membersNotBuilt={view.membersNotBuilt} />
        ) : (
          view.joinPlan.length > 0 && (
            <JoinPlanSection plan={view.joinPlan} membersNotBuilt={view.membersNotBuilt} />
          )
        )}
        {view.splitOff ? (
          <SplitOffSection outcome={view.splitOff} />
        ) : (
          view.splitPlan.length > 0 && <SplitPlanSection plan={view.splitPlan} />
        )}
        {view.catalogueJoins && <CatalogueJoinsSection joins={view.catalogueJoins} />}
        {view.catalogueSplitOffs && (
          <CatalogueSplitOffsSection splits={view.catalogueSplitOffs} outcome={view.splitOff} />
        )}
        {(view.bookingMoves || view.bookingMoveTaskIds.length > 0) && (
          <BookingMovesSection moves={view.bookingMoves} taskIds={view.bookingMoveTaskIds} />
        )}
        {view.catalogueJoins &&
          (view.catalogueJoins.bookingMoves || catalogueMoveTaskIds(view.catalogueJoins).length > 0) && (
            <BookingMovesSection
              title="Booking moves of catalogue joins"
              note="Edit-booking tasks moving the future bookings of products whose join this import wrote, off the tours their IDs left; each started as the user who saved the join."
              moves={view.catalogueJoins.bookingMoves}
              taskIds={catalogueMoveTaskIds(view.catalogueJoins)}
            />
          )}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

type ChipColor = 'default' | 'success' | 'warning' | 'error';
interface Count {
  label: string;
  n: number;
  color?: ChipColor;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One line of counts (zero counts are left out) that expands to a table. */
function Section({
  title,
  counts,
  note,
  head,
  children,
}: {
  title: string;
  counts: Count[];
  /** One sentence above the table: what the rows are. */
  note?: ReactNode;
  head: string[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {counts
          .filter((c) => c.n > 0)
          .map((c) => (
            <Chip key={c.label} size="small" variant="outlined" color={c.color ?? 'default'} label={c.label} />
          ))}
        <Button
          size="small"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} ${title.toLowerCase()}`}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ minWidth: 0 }}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      <Collapse in={open} unmountOnExit>
        {note && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {note}
          </Typography>
        )}
        <Box sx={{ mt: 0.5, maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {head.map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}

/** A tour, linked to its page (a new tab: the task details stay open). */
function TourLink({ id, name }: { id: number | null | undefined; name?: string | null }) {
  if (id == null) return <>{name || 'a deleted tour'}</>;
  return (
    <Link component={RouterLink} to={`/tours/${id}`} target="_blank" rel="noopener">
      {name ? `${name} (#${id})` : `tour #${id}`}
    </Link>
  );
}

function Holder({ holder }: { holder: SyncProductsHolder | null }) {
  if (!holder) return <>—</>;
  return (
    <>
      <TourLink id={holder.tour_id} name={holder.tour_name} />
      {holder.kind === 'primary' && ' (its own ID'}
      {holder.kind === 'alias' && ' (an additional ID'}
      {holder.kind === 'primary' && holder.parked_as && (
        <>
          , becomes <code>{holder.parked_as}</code>
        </>
      )}
      {holder.kind !== null && ')'}
    </>
  );
}

/** A background task, linked to its details (a new tab, like the tours). */
function TaskLink({ id }: { id: string }) {
  return (
    <Link component={RouterLink} to={`/bgtasks/${id}`} target="_blank" rel="noopener">
      <code>{id}</code>
    </Link>
  );
}

const Code = ({ children }: { children: ReactNode }) => <code>{children}</code>;

/** IDs as code, comma-separated. */
function Ids({ ids }: { ids: readonly string[] }) {
  return (
    <>
      {ids.map((id, i) => (
        <Fragment key={id}>
          {i > 0 && ', '}
          <Code>{id}</Code>
        </Fragment>
      ))}
    </>
  );
}

/** The other IDs a split-off moved with its key, and those it left on the old
 *  tour (`aliases` / `aliases_left`, catalogue mode only). */
function SplitAliases({
  moved,
  left,
  from,
}: {
  moved?: readonly string[];
  left?: readonly string[];
  from: ReactNode;
}) {
  return (
    <>
      {moved && moved.length > 0 && (
        <>
          with <Ids ids={moved} />
        </>
      )}
      {moved && moved.length > 0 && left && left.length > 0 && '; '}
      {left && left.length > 0 && (
        <>
          left on {from}: <Ids ids={left} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function OmittedSection({ omitted }: { omitted: SyncProductsOmitted[] }) {
  return (
    <Section
      title="Not built"
      counts={[{ label: `${plural(omitted.length, 'product', 'products')} left out`, n: omitted.length, color: 'warning' }]}
      note="Their tour key is an additional ID of another tour: building them would overwrite that tour."
      head={['Product', 'Key', 'Additional ID of']}
    >
      {omitted.map((o) => (
        <TableRow key={o.product_id}>
          <TableCell>
            <Code>{o.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{o.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={o.tour_id} name={o.tour_name} />
          </TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

const PLAN_LABEL: Record<string, string> = {
  create: 'new additional ID',
  already: 'already there',
  move: 'moves here',
};

function JoinPlanSection({ plan, membersNotBuilt }: { plan: SyncProductsJoinPlan[]; membersNotBuilt: number }) {
  const values = plan.flatMap((g) => g.members.flatMap((m) => m.ext_ids));
  const count = (action: string | null) => values.filter((e) => e.action === action).length;
  const problems = plan.reduce((n, g) => n + g.problems.length, 0);
  return (
    <Section
      title="Combined"
      counts={[
        { label: plural(plan.length, 'group', 'groups'), n: plan.length },
        { label: `${membersNotBuilt} joined, not built`, n: membersNotBuilt },
        { label: `${count('create')} new`, n: count('create') },
        { label: `${count('already')} already there`, n: count('already') },
        { label: `${count('move')} moving`, n: count('move') },
        { label: plural(problems, 'problem', 'problems'), n: problems, color: 'error' },
      ]}
      head={['Product', 'ID', 'Plan', 'Held by now']}
    >
      {plan.map((g) => (
        <Fragment key={g.into_product_id}>
          <TableRow>
            <TableCell colSpan={4} sx={{ bgcolor: 'action.hover' }}>
              Into <Code>{g.into_product_id}</Code>
              {g.key && (
                <>
                  , keyed <Code>{g.key}</Code>
                </>
              )}
              :{' '}
              {g.tour_id != null ? (
                <>
                  updates <TourLink id={g.tour_id} name={g.tour_name} />
                </>
              ) : (
                'creates a new tour'
              )}
            </TableCell>
          </TableRow>
          {g.members.flatMap((m) =>
            m.ext_ids.map((e) => (
              <TableRow key={`${m.product_id}\u0000${e.custom_id}`}>
                <TableCell>
                  <Code>{m.product_id}</Code>
                </TableCell>
                <TableCell>
                  <Code>{e.custom_id}</Code>
                </TableCell>
                <TableCell>{e.action ? PLAN_LABEL[e.action] ?? e.action : 'not joined'}</TableCell>
                <TableCell>
                  <Holder holder={e.owner} />
                </TableCell>
              </TableRow>
            )),
          )}
          {g.problems.map((p, i) => (
            <TableRow key={`problem-${i}`}>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="error">
                  {p.detail}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </Fragment>
      ))}
    </Section>
  );
}

function JoinsSection({ joins, membersNotBuilt }: { joins: SyncProductsJoinsOutcome; membersNotBuilt: number }) {
  const into = (e: { product_id: string; into_product_id: string }) => (
    <>
      <Code>{e.product_id}</Code> → <Code>{e.into_product_id}</Code>
    </>
  );
  return (
    <Section
      title="Combined"
      counts={[
        { label: `${membersNotBuilt} joined, not built`, n: membersNotBuilt },
        { label: `${joins.created.length} added`, n: joins.created.length, color: 'success' },
        { label: `${joins.already.length} already there`, n: joins.already.length },
        { label: `${joins.moved.length} moved`, n: joins.moved.length, color: 'success' },
        { label: `${joins.skipped.length} skipped`, n: joins.skipped.length, color: 'warning' },
        { label: plural(joins.errors.length, 'error', 'errors'), n: joins.errors.length, color: 'error' },
      ]}
      note="Each joined product's IDs, as additional IDs of the tour its group created."
      head={['Outcome', 'Product → joins', 'ID', 'Tour', 'Detail']}
    >
      {joins.created.map((e) => (
        <TableRow key={`c-${e.product_id}-${e.custom_id}`}>
          <TableCell>added</TableCell>
          <TableCell>{into(e)}</TableCell>
          <TableCell>
            <Code>{e.custom_id}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={e.tour_id} name={e.tour_name} />
          </TableCell>
          <TableCell />
        </TableRow>
      ))}
      {joins.already.map((e) => (
        <TableRow key={`a-${e.product_id}-${e.custom_id}`}>
          <TableCell>already there</TableCell>
          <TableCell>{into(e)}</TableCell>
          <TableCell>
            <Code>{e.custom_id}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={e.tour_id} name={e.tour_name} />
          </TableCell>
          <TableCell />
        </TableRow>
      ))}
      {joins.moved.map((e) => (
        <TableRow key={`m-${e.product_id}-${e.custom_id}`}>
          <TableCell>moved</TableCell>
          <TableCell>{into(e)}</TableCell>
          <TableCell>
            <Code>{e.custom_id}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={e.tour_id} name={e.tour_name} />
          </TableCell>
          <TableCell>
            from <TourLink id={e.from_tour_id} name={e.from_tour_name} />
            {e.kind === 'primary' && e.parked_as && (
              <>
                , whose own ID is now <Code>{e.parked_as}</Code>
              </>
            )}
            {e.taken_over && '; no longer maintained by the import'}
            {e.move_bookings && '; its future bookings move too'}
          </TableCell>
        </TableRow>
      ))}
      {joins.skipped.map((e) => (
        <TableRow key={`s-${e.product_id}-${e.custom_id}`}>
          <TableCell>skipped</TableCell>
          <TableCell>{into(e)}</TableCell>
          <TableCell>
            <Code>{e.custom_id}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={e.tour_id} name={e.tour_name} />
          </TableCell>
          <TableCell>
            now held by <Holder holder={e.owner} />: left there
          </TableCell>
        </TableRow>
      ))}
      {joins.errors.map((e, i) => (
        <TableRow key={`e-${i}`}>
          <TableCell>
            <Typography variant="body2" color="error">
              error
            </Typography>
          </TableCell>
          <TableCell>{into(e)}</TableCell>
          <TableCell>{e.custom_id ? <Code>{e.custom_id}</Code> : '—'}</TableCell>
          <TableCell>{e.tour_id != null && <TourLink id={e.tour_id} name={e.tour_name} />}</TableCell>
          <TableCell>{e.detail}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

function SplitPlanSection({ plan }: { plan: SyncProductsSplitOffPlan[] }) {
  return (
    <Section
      title="Split off"
      counts={[{ label: `${plan.length} planned`, n: plan.length }]}
      note="Each is built as its own tour, keyed by its ID; the tour that holds the ID now stops using it (and its other IDs listed, which move with it)."
      head={['Product', 'Key', 'Split off', 'Other IDs']}
    >
      {plan.map((s) => (
        <TableRow key={s.product_id}>
          <TableCell>
            <Code>{s.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{s.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={s.from_tour_id} name={s.from_tour_name} />
          </TableCell>
          <TableCell>{s.aliases?.length ? <Ids ids={s.aliases} /> : '—'}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

function SplitOffSection({ outcome }: { outcome: SyncProductsSplitOffOutcome }) {
  return (
    <Section
      title="Split off"
      counts={[
        { label: `${outcome.done.length} split off`, n: outcome.done.length, color: 'success' },
        {
          label: `${outcome.orphaned.length} waiting for a re-run`,
          n: outcome.orphaned.length,
          color: 'warning',
        },
        { label: plural(outcome.errors.length, 'error', 'errors'), n: outcome.errors.length, color: 'error' },
      ]}
      head={['Outcome', 'Product', 'Key', 'Split off', 'New tour', 'Detail']}
    >
      {outcome.done.map((s) => (
        <TableRow key={`d-${s.product_id}`}>
          <TableCell>split off</TableCell>
          <TableCell>
            <Code>{s.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{s.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={s.from_tour_id} name={s.from_tour_name} />
          </TableCell>
          <TableCell>
            <TourLink id={s.tour_id} name={s.tour_name} />
          </TableCell>
          <TableCell>
            <SplitAliases
              moved={s.aliases}
              left={s.aliases_left}
              from={<TourLink id={s.from_tour_id} name={s.from_tour_name} />}
            />
          </TableCell>
        </TableRow>
      ))}
      {outcome.orphaned.map((s) => (
        <TableRow key={`o-${s.product_id}`}>
          <TableCell>waiting</TableCell>
          <TableCell>
            <Code>{s.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{s.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={s.from_tour_id} name={s.from_tour_name} />
          </TableCell>
          <TableCell>
            <TourLink id={s.tour_id} name={s.tour_name} />, keyed <Code>{s.temp_key}</Code>
          </TableCell>
          <TableCell>{s.detail}</TableCell>
        </TableRow>
      ))}
      {outcome.errors.map((s) => (
        <TableRow key={`e-${s.product_id}`}>
          <TableCell>
            <Typography variant="body2" color="error">
              error
            </Typography>
          </TableCell>
          <TableCell>
            <Code>{s.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{s.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={s.from_tour_id} name={s.from_tour_name} />
          </TableCell>
          <TableCell />
          <TableCell>{s.detail}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

/** The Edit-booking tasks the catalogue joins launched, as `booking_move_task_ids`
 *  of each written join (in order, once each). */
export function catalogueMoveTaskIds(joins: CatalogueJoinsView): string[] {
  return Array.from(new Set(joins.joined.flatMap((j) => idsOf(j.booking_move_task_ids))));
}

/** Why a post-import step did not run: the soft time limit, or an error. */
function stepStopped(stopped: string | null, error: string | null, retry: string): string | null {
  if (stopped) return `Stopped at the time limit before it finished. ${retry}`;
  if (error) return `Failed: ${error}. ${retry}`;
  return null;
}

/** Catalogue mode: the joins of products whose `tour.join` names a product this
 *  run imported, written by the import (`post_import.catalogue_joins`). */
function CatalogueJoinsSection({ joins }: { joins: CatalogueJoinsView }) {
  const refusedProducts = new Set(joins.refused.map((r) => r.product_id)).size;
  const recreated = joins.joined.filter((j) => j.recreated).length;
  const stopped = stepStopped(joins.stopped, joins.error, 'The next sync of the joined products retries every join.');
  const rows =
    joins.joined.length +
    joins.unchanged.length +
    joins.deferred.length +
    joins.refused.length +
    joins.bookingMovesSkipped.length;
  if (stopped && rows === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Catalogue joins
        </Typography>
        <Typography variant="body2" color="error">
          {stopped}
        </Typography>
      </Stack>
    );
  }
  return (
    <Section
      title="Catalogue joins"
      counts={[
        { label: `${joins.joined.length} written`, n: joins.joined.length, color: 'success' },
        { label: `${recreated} re-created`, n: recreated, color: 'warning' },
        { label: `${joins.unchanged.length} already there`, n: joins.unchanged.length },
        { label: `${joins.deferred.length} waiting for their tour`, n: joins.deferred.length },
        { label: `${plural(refusedProducts, 'product', 'products')} refused`, n: refusedProducts, color: 'error' },
        {
          label: `${plural(joins.bookingMovesSkipped.length, 'booking move', 'booking moves')} skipped`,
          n: joins.bookingMovesSkipped.length,
          color: 'warning',
        },
      ]}
      note={
        <>
          Products whose Joins the tour of names a product this run imported: their IDs, written onto its tour as
          the user who saved each join. A refused one is marked join_refused; the next sync of its target retries it.
          {stopped && (
            <Typography component="span" variant="caption" color="error">
              {' '}
              {stopped}
            </Typography>
          )}
        </>
      }
      head={['Outcome', 'Product → joins', 'IDs', 'Tour', 'Detail']}
    >
      {joins.joined.map((j) => (
        <TableRow key={`j-${j.product_id}`}>
          <TableCell>written</TableCell>
          <TableCell>
            <Code>{j.product_id}</Code> → <Code>{j.into_product_id}</Code>
          </TableCell>
          <TableCell>
            <Ids ids={[...j.created, ...j.moved.map((m) => m.custom_id)]} />
          </TableCell>
          <TableCell>
            <TourLink id={j.tour_id} name={j.tour_name} />
          </TableCell>
          <TableCell>
            <CatalogueJoinedDetail joined={j} />
          </TableCell>
        </TableRow>
      ))}
      {joins.unchanged.map((id) => (
        <TableRow key={`u-${id}`}>
          <TableCell>already there</TableCell>
          <TableCell>
            <Code>{id}</Code>
          </TableCell>
          <TableCell colSpan={3} />
        </TableRow>
      ))}
      {joins.deferred.map((id) => (
        <TableRow key={`d-${id}`}>
          <TableCell>waiting</TableCell>
          <TableCell>
            <Code>{id}</Code>
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell>The product it joins has no tour yet: its IDs join that tour when a sync creates it.</TableCell>
        </TableRow>
      ))}
      {joins.refused.map((r, i) => (
        <TableRow key={`r-${i}`}>
          <TableCell>
            <Typography variant="body2" color="error">
              refused
            </Typography>
          </TableCell>
          <TableCell>
            <Code>{r.product_id}</Code> → <Code>{r.into_product_id}</Code>
          </TableCell>
          <TableCell />
          <TableCell>{r.tour_id != null && <TourLink id={r.tour_id} name={r.tour_name} />}</TableCell>
          <TableCell>{r.detail}</TableCell>
        </TableRow>
      ))}
      {joins.bookingMovesSkipped.map((m, i) => (
        <TableRow key={`b-${i}`}>
          <TableCell>booking move skipped</TableCell>
          <TableCell>
            <Code>{m.product_id}</Code>
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell>{m.detail}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

/** A written catalogue join's detail, `; `-separated: IDs it put back that the
 *  join held before (`recreated`), the IDs moved off other tours, and the
 *  booking moves it launched. */
function CatalogueJoinedDetail({ joined: j }: { joined: SyncProductsCatalogueJoined }) {
  const tasks = idsOf(j.booking_move_task_ids);
  const parts: ReactNode[] = [];
  if (j.recreated) {
    parts.push(
      're-created IDs it held before — to detach it, clear its join instead of deleting its IDs',
    );
  }
  for (const m of j.moved) {
    parts.push(
      <>
        <Code>{m.custom_id}</Code> moved from{' '}
        <TourLink id={m.from_tour_id} name={m.from_tour_name} />
        {m.kind === 'primary' && m.parked_as && (
          <>
            , whose own ID is now <Code>{m.parked_as}</Code>
          </>
        )}
      </>,
    );
  }
  if (tasks.length > 0) {
    parts.push(
      <>
        bookings moving:{' '}
        {tasks.map((id, i) => (
          <Fragment key={id}>
            {i > 0 && ', '}
            <TaskLink id={id} />
          </Fragment>
        ))}
      </>,
    );
  }
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && '; '}
          {p}
        </Fragment>
      ))}
    </>
  );
}

/** Catalogue mode: the imported products whose join was cleared
 *  (`post_import.split_offs`): split off (`cleared`, linked to the split-off the
 *  run made when it made one) or still pending. */
function CatalogueSplitOffsSection({
  splits,
  outcome,
}: {
  splits: CatalogueSplitOffsView;
  outcome: SyncProductsSplitOffOutcome | null;
}) {
  const stopped = stepStopped(splits.stopped, splits.error, 'The next sync of these products settles them.');
  const done = new Map((outcome?.done ?? []).map((d) => [d.product_id, d]));
  const tourNames = new Map<number, string | null | undefined>();
  for (const s of [...(outcome?.done ?? []), ...(outcome?.orphaned ?? []), ...(outcome?.errors ?? [])]) {
    tourNames.set(s.from_tour_id, s.from_tour_name);
  }
  if (stopped && splits.cleared.length + splits.pending.length === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Cleared joins
        </Typography>
        <Typography variant="body2" color="error">
          {stopped}
        </Typography>
      </Stack>
    );
  }
  return (
    <Section
      title="Cleared joins"
      counts={[
        { label: `${splits.cleared.length} split off`, n: splits.cleared.length, color: 'success' },
        { label: `${splits.pending.length} still pending`, n: splits.pending.length, color: 'warning' },
      ]}
      note={
        <>
          Products whose Joins the tour of was cleared while their IDs sat on its tour. Split off: the key left
          that tour (see Split off). Still pending: the next sync retries.
          {stopped && (
            <Typography component="span" variant="caption" color="error">
              {' '}
              {stopped}
            </Typography>
          )}
        </>
      }
      head={['Outcome', 'Product', 'Key', 'Old tour', 'New tour', 'Detail']}
    >
      {splits.cleared.map((id) => {
        const d = done.get(id);
        return (
          <TableRow key={`c-${id}`}>
            <TableCell>split off</TableCell>
            <TableCell>
              <Code>{id}</Code>
            </TableCell>
            <TableCell>{d ? <Code>{d.key}</Code> : '—'}</TableCell>
            <TableCell>{d ? <TourLink id={d.from_tour_id} name={d.from_tour_name} /> : '—'}</TableCell>
            <TableCell>{d ? <TourLink id={d.tour_id} name={d.tour_name} /> : '—'}</TableCell>
            <TableCell>
              {d ? (
                <SplitAliases
                  moved={d.aliases}
                  left={d.aliases_left}
                  from={<TourLink id={d.from_tour_id} name={d.from_tour_name} />}
                />
              ) : (
                'Its key is no longer an additional ID of that tour (moved since).'
              )}
            </TableCell>
          </TableRow>
        );
      })}
      {splits.pending.map((p) => (
        <TableRow key={`p-${p.product_id}`}>
          <TableCell>
            <Typography variant="body2" color="warning.main">
              still pending
            </Typography>
          </TableCell>
          <TableCell>
            <Code>{p.product_id}</Code>
          </TableCell>
          <TableCell>
            <Code>{p.key}</Code>
          </TableCell>
          <TableCell>
            <TourLink id={p.from_tour_id} name={tourNames.get(p.from_tour_id)} />
          </TableCell>
          <TableCell />
          <TableCell>{p.detail}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}

/** One booking move's product, the tour its bookings leave and the tour they
 *  join. */
function MoveCells({
  move,
}: {
  move: Pick<SyncProductsBookingMoveLaunch, 'product_id' | 'from_tour_id' | 'from_tour_name' | 'tour_id' | 'tour_name'>;
}) {
  return (
    <>
      <TableCell>
        <Code>{move.product_id}</Code>
      </TableCell>
      <TableCell>
        <TourLink id={move.from_tour_id} name={move.from_tour_name} />
      </TableCell>
      <TableCell>
        <TourLink id={move.tour_id} name={move.tour_name} />
      </TableCell>
    </>
  );
}

/** The booking moves (D5): each Edit-booking task launched, with its product,
 *  its tours and how many bookings it was handed, and each requested move that
 *  launched none. `taskIds` (`booking_move_task_ids`) are the launched tasks in
 *  launch order; one the `booking_moves` entries do not cover is still listed. */
function BookingMovesSection({
  moves,
  taskIds,
  title = 'Booking moves',
  note = 'Edit-booking tasks moving the future bookings of combined products off their old tours; each links back to this sync.',
}: {
  moves: SyncProductsBookingMoves | null;
  taskIds: string[];
  title?: string;
  note?: string;
}) {
  const launched = moves?.launched ?? [];
  const skipped = moves?.skipped ?? [];
  const errors = moves?.errors ?? [];
  const covered = new Set(launched.map((m) => m.task_id));
  const unlisted = taskIds.filter((id) => !covered.has(id));
  const tasks = launched.length + unlisted.length;
  return (
    <Section
      title={title}
      counts={[
        { label: `${plural(tasks, 'task', 'tasks')} launched`, n: tasks, color: 'success' },
        { label: `${skipped.length} with nothing to move`, n: skipped.length },
        { label: plural(errors.length, 'error', 'errors'), n: errors.length, color: 'error' },
      ]}
      note={note}
      head={['Outcome', 'Product', 'From', 'To', 'Bookings', 'Task or detail']}
    >
      {launched.map((m) => (
        <TableRow key={`l-${m.task_id}`}>
          <TableCell>launched</TableCell>
          <MoveCells move={m} />
          <TableCell>{m.count}</TableCell>
          <TableCell>
            <TaskLink id={m.task_id} />
          </TableCell>
        </TableRow>
      ))}
      {unlisted.map((id) => (
        <TableRow key={`t-${id}`}>
          <TableCell>launched</TableCell>
          <TableCell colSpan={4} />
          <TableCell>
            <TaskLink id={id} />
          </TableCell>
        </TableRow>
      ))}
      {skipped.map((m, i) => (
        <TableRow key={`s-${i}`}>
          <TableCell>nothing to move</TableCell>
          <MoveCells move={m} />
          <TableCell>0</TableCell>
          <TableCell>{m.detail}</TableCell>
        </TableRow>
      ))}
      {errors.map((m, i) => (
        <TableRow key={`e-${i}`}>
          <TableCell>
            <Typography variant="body2" color="error">
              error
            </Typography>
          </TableCell>
          <MoveCells move={m} />
          <TableCell />
          <TableCell>{m.detail}</TableCell>
        </TableRow>
      ))}
    </Section>
  );
}
